import React from "react";
import { fmtMoney, fmtDays, fmtYears, fmtPct } from "@/lib/chart-utils";

/**
 * VITAE-PUBLICA report template — rendered off-screen for html2canvas capture.
 * Each <section data-report-section> becomes a PDF page (or a tiled run of pages).
 *
 * The root is absolutely positioned off-screen at a fixed 794px width
 * (A4 portrait @ ~96 DPI minus margins), so the layout is consistent
 * regardless of the viewer's viewport.
 */

const PAGE_STYLE = {
  width: "794px",
  padding: "48px 56px",
  backgroundColor: "#FFFFFF",
  color: "#0B1A2B",
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: "13px",
  lineHeight: 1.55,
  boxSizing: "border-box",
};

const DISCLAIMER_STYLE = {
  border: "2px solid #C0392B",
  borderRadius: "6px",
  padding: "10px 14px",
  fontSize: "11px",
  color: "#7A1F17",
  backgroundColor: "#FDECEA",
  margin: "16px 0",
};

function Disclaimer() {
  return (
    <div style={DISCLAIMER_STYLE}>
      <strong>⚠ DOCUMENT PÉDAGOGIQUE —</strong> Ce rapport est un outil d'aide à
      la réflexion conforme à la Nouvelle Théorie des Finances Publiques (ELY
      Mustapha). Il n'a AUCUNE valeur juridictionnelle.
    </div>
  );
}

function KPI({ label, value, accent = "#1A3C5E" }) {
  return (
    <div
      style={{
        border: `1px solid ${accent}33`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: "4px",
        padding: "10px 12px",
        backgroundColor: `${accent}08`,
      }}
    >
      <div
        style={{
          fontSize: "10px",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "#6b7280",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "20px", fontWeight: 800, color: accent, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 800,
        color: "#1A3C5E",
        borderBottom: "2px solid #C9A84C",
        paddingBottom: "4px",
        margin: "0 0 14px 0",
      }}
    >
      {children}
    </h2>
  );
}

function Table({ headers, rows }) {
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px",
      }}
    >
      <thead>
        <tr style={{ backgroundColor: "#F3EBD0" }}>
          {headers.map((h, i) => (
            <th
              key={i}
              style={{
                textAlign: "left",
                padding: "6px 8px",
                border: "1px solid #D9CDA2",
                fontWeight: 700,
                color: "#1A3C5E",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td
                key={j}
                style={{
                  padding: "5px 8px",
                  border: "1px solid #E5E7EB",
                  verticalAlign: "top",
                }}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Seal() {
  return (
    <svg width="80" height="80" viewBox="0 0 120 120" aria-hidden="true">
      <circle cx="60" cy="60" r="56" fill="#FBF6E6" stroke="#1A3C5E" strokeWidth="2" />
      <circle cx="60" cy="60" r="50" fill="none" stroke="#1A3C5E" strokeWidth="0.6" strokeDasharray="2 2" />
      <g transform="translate(60,62)" stroke="#1A3C5E" strokeWidth="1.2" fill="none">
        <line x1="0" y1="-20" x2="0" y2="18" />
        <line x1="-18" y1="-14" x2="18" y2="-14" />
        <path d="M -22 -10 A 4 2 0 0 0 -14 -10 L -18 -4 Z" fill="#C9A84C" />
        <path d="M 14 -10 A 4 2 0 0 0 22 -10 L 18 -4 Z" fill="#C9A84C" />
        <path d="M -6 4 L 6 4 L -6 18 L 6 18 Z" fill="#C0392B" fillOpacity="0.5" />
      </g>
    </svg>
  );
}

export default function ReportTemplate({ project, analysis }) {
  const r = analysis?.results || {};
  const currency = r.summary?.currency || "UM";
  const profiles = r.profiles || [];
  const ind0 = profiles[0]?.individual || {};
  const budgetLines = r.aggregated_budget_lines || [];
  const debt = r.debt || {};
  const national = r.national || {};
  const legal = r.legal_analysis;
  const judgment = r.judgment;
  const today = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div
      id="vp-report-template"
      style={{
        position: "absolute",
        left: "-10000px",
        top: 0,
        zIndex: -1,
      }}
      data-testid="report-template-root"
    >
      {/* Page 1 — Title */}
      <section data-report-section="title" style={PAGE_STYLE}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "640px",
            textAlign: "center",
          }}
        >
          <Seal />
          <div
            style={{
              fontSize: "40px",
              fontWeight: 900,
              color: "#1A3C5E",
              marginTop: "30px",
              letterSpacing: "0.04em",
            }}
          >
            VITAE-PUBLICA™
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#1A3C5E",
              marginTop: "8px",
              maxWidth: "560px",
            }}
          >
            Rapport d'Analyse Vitaliste des Finances Publiques
          </div>
          <div
            style={{
              marginTop: "34px",
              fontSize: "13px",
              fontStyle: "italic",
              color: "#374151",
            }}
          >
            {project?.country || "—"} &nbsp;|&nbsp; Exercice{" "}
            {project?.fiscal_year || "—"} &nbsp;|&nbsp; {today}
          </div>
          <div style={{ marginTop: "40px", fontSize: "13px" }}>
            <div style={{ color: "#1A3C5E", fontWeight: 700 }}>
              Auteur de la méthodologie : Ahmed ELY Mustapha
            </div>
            <div
              style={{
                marginTop: 4,
                color: "#6b7280",
                fontStyle: "italic",
              }}
            >
              Programmeur : ELY Mustapha — PMP · I-PMP · IBM Full Stack Developer
            </div>
          </div>
          <div style={{ marginTop: "32px", maxWidth: "560px" }}>
            <Disclaimer />
          </div>
        </div>
      </section>

      {/* Page 2 — Synthèse exécutive */}
      <section data-report-section="summary" style={PAGE_STYLE}>
        <SectionTitle>Synthèse exécutive</SectionTitle>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "10px",
          }}
        >
          <KPI
            label="Jours de vie sacrifiés / an"
            value={fmtDays(ind0.jours_vie_sacrifies_par_an)}
            accent="#C0392B"
          />
          <KPI
            label="Mois de vie sacrifiés"
            value={ind0.mois_vie_sacrifies != null ? `${ind0.mois_vie_sacrifies} mois` : "—"}
            accent="#D97706"
          />
          <KPI
            label="Années carrière sacrifiées"
            value={fmtYears(ind0.annees_vie_sur_carriere)}
            accent="#C9A84C"
          />
          <KPI
            label="% vie fiscale"
            value={fmtPct(ind0.ratio_vie_fiscale_pct)}
            accent="#1A3C5E"
          />
          <KPI
            label="Années vie hypothéquées"
            value={fmtYears(debt.annees_vie_hypothequees)}
            accent="#111827"
          />
          <KPI
            label="Budget national (années cumulées)"
            value={fmtYears(national.annees_vies_budget)}
            accent="#0F766E"
          />
        </div>
        <div style={{ marginTop: "14px", fontSize: "12px" }}>
          <strong>Profil analysé :</strong>{" "}
          {profiles[0]?.profile_name || "—"}. Devise : {currency}. Formule
          canonique : <em>Jours_de_vie = Prélèvements / (Salaire_mensuel / 30)</em>.
        </div>
      </section>

      {/* Pages 3-4 — Profils */}
      <section data-report-section="profiles" style={PAGE_STYLE}>
        <SectionTitle>Profil du contribuable modèle</SectionTitle>
        {profiles.map((p) => {
          const i = p.individual || {};
          return (
            <div key={p.profile_id} style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#C9A84C",
                  margin: "0 0 6px 0",
                }}
              >
                {p.profile_name || "Profil"}
              </h3>
              <Table
                headers={["Paramètre", "Valeur"]}
                rows={[
                  ["Salaire mensuel", fmtMoney(i.salaire_mensuel, currency)],
                  ["Salaire journalier (= Mensuel / 30)", fmtMoney(i.salaire_journalier, currency)],
                  ["Total prélèvements / an", fmtMoney(i.total_prelevements, currency)],
                  ["Jours de vie sacrifiés / an (= Total / Journalier)", fmtDays(i.jours_vie_sacrifies_par_an)],
                  ["Mois de vie sacrifiés", i.mois_vie_sacrifies != null ? `${i.mois_vie_sacrifies} mois` : "—"],
                  ["Années carrière sacrifiées", fmtYears(i.annees_vie_sur_carriere)],
                  ["% vie fiscale", fmtPct(i.ratio_vie_fiscale_pct)],
                ]}
              />
            </div>
          );
        })}
      </section>

      {/* Pages 5-7 — Budget national */}
      <section data-report-section="budget" style={PAGE_STYLE}>
        <SectionTitle>Budget national — conversion en vies humaines</SectionTitle>
        {national.available ? (
          <p style={{ fontSize: "13px" }}>
            Le budget national agrégé représente{" "}
            <strong>{fmtYears(national.annees_vies_budget)}</strong> d'existence
            humaine cumulées.
          </p>
        ) : (
          <p style={{ fontSize: "12px", color: "#6b7280" }}>
            Données nationales non disponibles pour cet exercice.
          </p>
        )}
        <div style={{ marginTop: "10px" }}>
          <Table
            headers={["Secteur", `Montant (${currency})`, "%"]}
            rows={budgetLines
              .slice(0, 20)
              .map((bl) => [
                bl.sector || "—",
                fmtMoney(bl.amount, currency),
                bl.percentage != null ? `${bl.percentage}%` : "—",
              ])}
          />
        </div>
      </section>

      {/* Pages 8-9 — Endettement */}
      <section data-report-section="debt" style={PAGE_STYLE}>
        <SectionTitle>Endettement public</SectionTitle>
        {debt.available ? (
          <Table
            headers={["Indicateur", "Valeur"]}
            rows={[
              ["Dette par contribuable", fmtMoney(debt.dette_par_contribuable, currency)],
              ["Années de vie hypothéquées", fmtYears(debt.annees_vie_hypothequees)],
            ]}
          />
        ) : (
          <p>Aucune donnée de dette publique exploitable.</p>
        )}
        <Disclaimer />
      </section>

      {/* Pages 10-12 — Analyse juridique */}
      {legal && (
        <section data-report-section="legal" style={PAGE_STYLE}>
          <SectionTitle>Analyse juridique</SectionTitle>
          {legal.synthese && (
            <p style={{ fontStyle: "italic", fontSize: "12px" }}>
              {legal.synthese}
            </p>
          )}
          {legal.infractions?.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <Table
                headers={["Qualification", "Montant", "Jours de vie", "Gravité"]}
                rows={legal.infractions.map((inf) => [
                  inf.qualification,
                  inf.montant_um != null ? fmtMoney(inf.montant_um, currency) : "—",
                  inf.jours_de_vie != null ? fmtDays(inf.jours_de_vie) : "—",
                  <span
                    key="g"
                    style={{
                      color:
                        inf.niveau_gravite === 3
                          ? "#C0392B"
                          : inf.niveau_gravite === 2
                          ? "#B87A1C"
                          : "#1F7A3E",
                      fontWeight: 700,
                    }}
                  >
                    Niveau {inf.niveau_gravite}
                  </span>,
                ])}
              />
            </div>
          )}
          {["professionnelle", "civile", "administrative", "penale"].map((k) => {
            const items = legal.responsabilites?.[k] || [];
            if (!items.length) return null;
            return (
              <div key={k} style={{ marginTop: "12px" }}>
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1A3C5E",
                    margin: "6px 0",
                    textTransform: "capitalize",
                  }}
                >
                  Responsabilité {k}
                </h3>
                <ul style={{ paddingLeft: "18px", margin: 0, fontSize: "12px" }}>
                  {items.map((it, idx) => (
                    <li key={idx} style={{ marginBottom: "4px" }}>
                      {it.description}
                      {it.articles?.length > 0 && (
                        <span
                          style={{ color: "#6b7280", fontStyle: "italic" }}
                        >
                          {" "}
                          — {it.articles.join(", ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      )}

      {/* Pages 13-15 — Jugement simulé */}
      {judgment && (
        <section data-report-section="judgment" style={PAGE_STYLE}>
          <SectionTitle>Jugement vitaliste simulé (document pédagogique)</SectionTitle>
          <div
            style={{
              textAlign: "center",
              fontFamily: "Merriweather, serif",
              marginBottom: "12px",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: "18px", color: "#1A3C5E" }}>
              {judgment.tribunal}
            </div>
            <div
              style={{
                fontWeight: 700,
                marginTop: "6px",
                letterSpacing: "0.12em",
                color: "#1A3C5E",
              }}
            >
              {judgment.header}
            </div>
          </div>
          <h3 style={{ fontSize: "13px", color: "#1A3C5E", margin: "8px 0 4px" }}>
            Exposé des faits — volet financier
          </h3>
          <p style={{ fontSize: "12px" }}>{judgment.expose_des_faits?.financier}</p>
          <h3 style={{ fontSize: "13px", color: "#1A3C5E", margin: "8px 0 4px" }}>
            Exposé des faits — volet vitaliste
          </h3>
          <p style={{ fontSize: "12px", fontWeight: 700 }}>
            {judgment.expose_des_faits?.vitaliste}
          </p>
          <h3 style={{ fontSize: "13px", color: "#1A3C5E", margin: "8px 0 4px" }}>
            Motifs vitalistes
          </h3>
          <p style={{ fontSize: "12px", fontStyle: "italic" }}>
            {judgment.motifs_vitalistes}
          </p>
          <h3 style={{ fontSize: "13px", color: "#1A3C5E", margin: "10px 0 4px" }}>
            Dispositif
          </h3>
          <Table
            headers={["Branche", "Décision"]}
            rows={[
              ["Sanctions pénales", judgment.dispositif?.sanctions_penales || "—"],
              [
                "Réparation civile",
                fmtMoney(judgment.dispositif?.reparation_civile_en_UM, currency),
              ],
              [
                "Réparation vitaliste",
                judgment.dispositif?.reparation_vitaliste || "—",
              ],
              [
                "Sanctions administratives",
                judgment.dispositif?.sanctions_administratives || "—",
              ],
            ]}
          />
          {judgment.citation_finale && (
            <p
              style={{
                marginTop: "14px",
                textAlign: "center",
                fontStyle: "italic",
                color: "#374151",
              }}
            >
              « {judgment.citation_finale} »
            </p>
          )}
          <Disclaimer />
        </section>
      )}

      {/* Page finale — Méthodologie */}
      <section data-report-section="method" style={PAGE_STYLE}>
        <SectionTitle>Méthodologie et références</SectionTitle>
        <p style={{ fontSize: "13px", fontWeight: 700 }}>
          Formule vitaliste canonique : Jours_de_vie = Prélèvements /
          (Salaire_mensuel / 30).
        </p>
        <p style={{ fontSize: "12px" }}>
          Unité de compte des finances publiques : <strong>la vie humaine</strong>.
          Unité de mesure : le jour de vie, dérivé du salaire mensuel de
          référence du contribuable.
        </p>
        <p style={{ fontSize: "12px", fontStyle: "italic" }}>
          Référence : ELY Mustapha, « La Nouvelle Théorie des Finances Publiques
          — Les Finances Publiques sont des Tranches de Vies ».
        </p>
        <Disclaimer />
        <div
          style={{
            marginTop: "30px",
            fontSize: "10px",
            color: "#6b7280",
            textAlign: "center",
          }}
        >
          Document généré par VITAE-PUBLICA™ le {today}.
        </div>
      </section>
    </div>
  );
}
